import { PrimeReactProvider } from "primereact/api";
import { ThemeProvider } from "./themes/Theme";
import "primereact/resources/primereact.min.css";
import "primeicons/primeicons.css";
import { Main } from "./pages/main/Main";
import {
  QueryClient,
  QueryClientProvider,
  QueryCache,
  MutationCache,
} from "@tanstack/react-query";
import { Route, HashRouter as Router, Routes } from "react-router-dom";
import { PrivacyModeProvider } from "./context-providers/Privacy";
import { ConfirmDialog } from "primereact/confirmdialog";
import { ToastProvider } from "./context-providers/Toast";
import * as Sentry from "@sentry/react";
import { ApiError } from "./client/core/ApiError";
import { getSentryTags, isCurrentUserDeveloper } from "./sentry-globals";
import { UpdateContextProvider } from "./context-providers/UpdateContext";

const augmentApiError = (scope: Sentry.Scope, err: ApiError) => {
  scope.setTags(getSentryTags());
  scope.setExtra("error_body", err.body);
  scope.setExtra("query", err.request.query);
  err.name += ": " + err.request.url + " - " + err.statusText;
};

const queryClient = new QueryClient({
  mutationCache: new MutationCache({
    onError: (err, _variables, _context, mutation) => {
      Sentry.withScope((scope) => {
        scope.setContext("mutation", {
          mutationId: mutation.mutationId,
          variables: mutation.state.variables,
        });
        if (mutation.options.mutationKey) {
          scope.setFingerprint(
            // Duplicate to prevent modification
            Array.from(mutation.options.mutationKey) as string[]
          );
        }
        if (err instanceof ApiError) {
          augmentApiError(scope, err);
        }
        Sentry.captureException(err);
      });
    },
  }),
  queryCache: new QueryCache({
    onError: (err, query) => {
      // Developers switch logins often, so stale tabs trigger 400s when they
      // request a report from the previous client under the new client's credentials.
      if (
        err instanceof ApiError &&
        err.status === 400 &&
        err.request.url === "/asgard/list_report/{report_id}" &&
        isCurrentUserDeveloper()
      ) {
        console.warn("Dropping developer list_report error");
        return;
      }
      Sentry.withScope((scope) => {
        scope.setContext("query", { queryHash: query.queryHash });
        scope.setFingerprint([query.queryHash.replaceAll(/[0-9]/g, "0")]);
        if (err instanceof ApiError) {
          augmentApiError(scope, err);
        }
        Sentry.captureException(err);
      });
    },
  }),
});

function App() {
  return (
    <Router>
      <PrimeReactProvider value={{ zIndex: { toast: 2000 } }}>
        <ThemeProvider>
          <PrivacyModeProvider>
            <QueryClientProvider client={queryClient}>
              <UpdateContextProvider>
                <ToastProvider>
                  <Routes>
                    <Route path="*" element={<Main />} />
                  </Routes>
                </ToastProvider>
              </UpdateContextProvider>
            </QueryClientProvider>
          </PrivacyModeProvider>
        </ThemeProvider>
        <ConfirmDialog />
      </PrimeReactProvider>
    </Router>
  );
}

export default App;
